/**
 * The "My Reports" AMOUNT entries — bonus, commission and reimbursement.
 *
 * One shape for all three. They differ only in which NetSuite payroll item the
 * posting step resolves, which is why {@link REPORT_DEFINITIONS} already treats
 * them as one concept with a `shape` of 'amount'. The hours reports (PTO, holiday)
 * are a genuinely different shape — a date RANGE that becomes a TimeBill — and live
 * in `pto.ts`.
 *
 * PURE and dependency-free, so the browser and the API enforce EXACTLY the same
 * rules. The API is still the authority: the browser calling this first is a
 * courtesy to the user, not a security boundary, and the API never trusts that it
 * ran.
 *
 * AMOUNTS ARE ALWAYS POSITIVE. Every one of these reports pays the employee, so a
 * negative is meaningless here rather than merely unusual. If a credit is ever
 * needed, the direction must be expressed by the posting step, never by a sign
 * buried in stored data where two code paths could disagree about whose job it is to
 * flip it — a sign error in payroll is silent and expensive.
 */
import { findReport } from './reports.js';
/** The three reports this module covers. */
export const AMOUNT_REPORT_IDS = ['bonus', 'commission', 'reimbursement'];
/** True when `id` is one of the amount reports (narrows the type). */
export function isAmountReportId(id) {
    return AMOUNT_REPORT_IDS.includes(id);
}
/**
 * Largest amount a single entry may carry.
 *
 * A ceiling exists because the realistic failure here is a typo — an extra digit
 * turning 500 into 5000 — and payroll pays what it is told. It is deliberately
 * generous rather than tuned: the goal is to catch a slipped keystroke, not to
 * second-guess what a legitimate bonus can be. The database's numeric(12,2) allows
 * far more; this is the human-scale bound.
 */
export const MAX_ENTRY_AMOUNT = 100_000;
/**
 * Is this value a whole number of hundredths (cents, or hundredths of an hour)?
 *
 * A STRICT `Math.round(v * 100) === v * 100` is WRONG and rejects real values:
 * `8.3 * 100` is 830.0000000000001 in binary floating point, so 8.3 — a figure that
 * appears on the actual driver sheet — would be refused as "too precise". Comparing
 * with a tolerance accepts it while still rejecting genuine over-precision (12.125
 * scales to 1212.5, which is half a unit away, not a rounding artefact).
 */
function isWholeHundredths(value) {
    const scaled = value * 100;
    return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}
/** Calendar date `YYYY-MM-DD`, no time part. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Does this `YYYY-MM-DD` name a date that actually exists?
 *
 * `Date.parse` is NOT sufficient and quietly gives the wrong answer: JavaScript
 * ROLLS OVER an out-of-range day, so `2026-02-31` parses happily as 3 March rather
 * than failing. A pay period silently moving to another month is exactly the sort of
 * error nobody notices until the wrong paycheck carries it, so the components are
 * round-tripped and compared instead.
 */
function isRealDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
        return false;
    }
    const parsed = new Date(Date.UTC(y, m - 1, d));
    return (parsed.getUTCFullYear() === y && parsed.getUTCMonth() === m - 1 && parsed.getUTCDate() === d);
}
/**
 * Validate an amount entry. Returns EVERY problem, not just the first, so a user
 * fixes one form rather than discovering faults one submit at a time.
 *
 * An empty array means valid.
 */
export function validateAmountEntry(input, context) {
    const issues = [];
    if (!context.activeEmployeeIds.has(input.employeeId)) {
        issues.push({ field: 'employeeId', message: 'Select an active employee.' });
    }
    if (!context.activeDepartmentIds.has(input.departmentId)) {
        issues.push({ field: 'departmentId', message: 'Select an active department.' });
    }
    if (!ISO_DATE.test(input.payPeriodEnding)) {
        issues.push({ field: 'payPeriodEnding', message: 'Pay period ending must be a date.' });
    }
    else if (!isRealDate(input.payPeriodEnding)) {
        issues.push({ field: 'payPeriodEnding', message: 'That date does not exist.' });
    }
    if (typeof input.amount !== 'number' || !Number.isFinite(input.amount)) {
        issues.push({ field: 'amount', message: 'Enter an amount.' });
    }
    else if (input.amount <= 0) {
        // Says what to DO rather than restating the rule, and names no report that
        // does not exist — this string is shown to a supervisor mid-entry.
        issues.push({ field: 'amount', message: 'Enter an amount greater than zero.' });
    }
    else if (!isWholeHundredths(input.amount)) {
        issues.push({ field: 'amount', message: 'Amounts are in whole cents.' });
    }
    else if (input.amount > MAX_ENTRY_AMOUNT) {
        issues.push({
            field: 'amount',
            message: `That is over the ${MAX_ENTRY_AMOUNT.toLocaleString()} per-entry limit — check for a typo.`,
        });
    }
    if (input.note !== undefined && input.note !== null && input.note.length > 500) {
        issues.push({ field: 'note', message: 'Note is limited to 500 characters.' });
    }
    return issues;
}
/**
 * The NetSuite EARNING KIND a report's amount posts under, or `null` when the
 * report does not post as an earning at all.
 *
 * `commission` maps to 'bonus' because that is genuinely how NetSuite classifies it
 * here — COMMISSION - SALES and Commission - Trailer Shop both carry `kind: 'bonus'`
 * in EARNING_MAP. Mapping it to a 'commission' kind that does not exist would
 * resolve to nothing and flag every commission entry.
 *
 * Every amount report resolves to a real earning kind, so this never returns null.
 * Deductions would have — they are not earnings and appear nowhere in EARNING_MAP —
 * but NetSuite handles deductions itself and WorkLogix does not collect them.
 */
export function earningKindForReport(reportId) {
    switch (reportId) {
        case 'bonus':
        case 'commission':
            return 'bonus';
        case 'reimbursement':
            return 'reimbursement';
    }
}
/** The report's display label, for messages. Falls back to the raw id. */
export function amountReportLabel(reportId) {
    return findReport(reportId)?.label ?? reportId;
}
/** Narrow a {@link ReportId} to an amount report, or undefined. */
export function asAmountReportId(id) {
    return isAmountReportId(id) ? id : undefined;
}
//# sourceMappingURL=amount-entry.js.map